/**
 * Auth Service Layer - 业务逻辑层 + Mock 适配
 * 页面组件只调用此 Service 层,不直接调用 API
 */

import { config } from '../../config';
import * as authMock from '../../mocks/auth';
import type { ApiResponse } from '../../types/common';
import type { LoginRequest, LoginResponse, UserInfo, UserInfoResponse, AccountInfo } from '../../types/auth';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';
import sha256 from 'crypto-js/sha256';

/**
 * 用户登录
 */
export async function login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
  if (config.mock) {
    return authMock.mockLogin(credentials);
  }

  const req = {
    deviceType: 'PC',
    deviceId: '',
    loginId: credentials.username,
    password: sha256(credentials.password).toString(),
  };
  return httpClient.post(API_ENDPOINTS.AUTH.LOGIN, req);
}

/**
 * 获取当前用户信息
 */
export async function getUserInfo(): Promise<ApiResponse<UserInfoResponse>> {
  if (config.mock) {
    return authMock.mockGetUserInfo();
  }

  return httpClient.post(API_ENDPOINTS.AUTH.USER_INFO);
}

/**
 * 获取用户个人资料
 */
export async function getUserProfile(): Promise<ApiResponse<AccountInfo>> {
  if (config.mock) {
    return authMock.mockGetAccountInfo();
  }

  return httpClient.get(API_ENDPOINTS.AUTH.ACCOUNT_INFO);
}

/**
 * 重置密码
 */
export async function resetPassword(data: {
  loginId: string;
  oldPassword: string;
  newPassword: string;
}): Promise<ApiResponse<void>> {
  if (config.mock) {
    return authMock.mockResetPassword(data);
  }

  return httpClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
    loginId: data.loginId,
    oldPassword: sha256(data.oldPassword).toString(),
    newPassword: sha256(data.newPassword).toString(),
  });
}

/**
 * 更新当前仓库
 */
export async function updateCurrentWarehouse(warehouseId: number): Promise<ApiResponse<void>> {
  if (config.mock) {
    return authMock.mockUpdateCurrentWarehouse(warehouseId);
  }

  return httpClient.post(API_ENDPOINTS.AUTH.UPDATE_WAREHOUSE, { warehouseId });
}

/**
 * 获取当前用户的菜单树
 */
export async function getMenu(): Promise<ApiResponse<any[]>> {
  if (config.mock) {
    return authMock.mockGetMenu();
  }

  return httpClient.get(API_ENDPOINTS.AUTH.MENU_TREE);
}

/**
 * SSO 登录 (MS-Entra) - mock 模式下直接返回模拟数据
 */
export async function ssoLogin(): Promise<ApiResponse<LoginResponse>> {
  if (config.mock) {
    return authMock.mockSsoLogin();
  }

  return httpClient.post(API_ENDPOINTS.AUTH.SSO_LOGIN);
}

/**
 * 获取 SSO OIDC 配置（authorize URL、clientId、scope）
 * 前端根据返回值动态构造 MS Entra authorize 跳转 URL
 */
export async function getSSOConfig(): Promise<ApiResponse<{
  enabled: boolean;
  authorizeUrl?: string;
  clientId?: string;
  scope?: string;
}>> {
  if (config.mock) {
    return {
      success: true,
      code: 200,
      data: {
        enabled: true,
        authorizeUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
        clientId: 'mock-client-id',
        scope: 'openid profile email',
      },
    } as any;
  }

  return httpClient.get(API_ENDPOINTS.AUTH.SSO_CONFIG);
}

/**
 * SSO 回调 - 用授权码换取 session
 * redirectUri 必须与 authorize 请求时一致，MS Entra 校验严格匹配
 */
export async function handleSSOCallback(code: string, deviceType: string = 'PC'): Promise<ApiResponse<LoginResponse>> {
  if (config.mock) {
    return authMock.mockSsoLogin();
  }

  return httpClient.post(API_ENDPOINTS.AUTH.SSO_CALLBACK, {
    code,
    deviceType,
    redirectUri: window.location.origin + '/login',
  });
}
